import React from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import "./app.css";
import ApplicationErrorBoundary from "./components/ApplicationErrorBoundary.jsx";
import ApplicationUserUiPreferencesProvider from "./components/ApplicationUserUiPreferencesProvider.jsx";
import appRouter, { AppRouterFallback } from "./components/AppRouter.jsx";
import DeploymentUpdateGuard from "./components/DeploymentUpdateGuard.jsx";
import { ToastProvider } from "./components/ResultsPrimitives.jsx";
import useAccess, { AccessProvider } from "./hooks/useAccess.jsx";
import { OptimizationJobProvider } from "./hooks/useOptimizationJobTracker.js";
import { NotificationProvider } from "./hooks/useNotifications.jsx";
import { GeotabLiveAlertsProvider } from "./components/GeotabLiveAlerts.jsx";
import { isBillingWorkspaceOnly } from "./lib/accessControl.js";
import { ResultsExportProvider } from "./hooks/useResultsExportTracker.jsx";
import { TruckSheetPdfExportProvider } from "./hooks/useTruckSheetPdfExportTracker.jsx";

// ─── Ancien système (référence transitoire) ───────────────────────────
// L'application utilisait readJsonScriptElement() / resolvePageBootstrap()
// pour lire les données JSON injectées par Jinja2 dans des balises <script>
// et résoudre le composant de page à monter (dashboard, results, etc.).
// Ce système est remplacé par un data router React Router qui charge
// les vues simples via des loaders partagés et garde le shell en layout racine.
// ────────────────────────────────────────────────────────────────────────

const REACT_ROOT_STATE_KEY = Symbol.for("transia.react-app-root-state");

function getReactRootState() {
  const existingState = globalThis[REACT_ROOT_STATE_KEY];
  if (existingState && typeof existingState === "object") {
    return existingState;
  }

  const nextState = {
    activeContainer: null,
    activeRoot: null,
  };
  globalThis[REACT_ROOT_STATE_KEY] = nextState;
  return nextState;
}

function getOrCreateRoot(container) {
  if (!(container instanceof HTMLElement)) {
    throw new Error("Le root React principal doit etre un HTMLElement.");
  }

  const rootState = getReactRootState();
  if (rootState.activeRoot && rootState.activeContainer === container) {
    return rootState.activeRoot;
  }

  if (rootState.activeRoot && rootState.activeContainer && rootState.activeContainer !== container) {
    rootState.activeRoot.unmount();
  }

  rootState.activeContainer = container;
  rootState.activeRoot = createRoot(container);
  return rootState.activeRoot;
}

function AccessBoundOptimizationJobProvider({ children }) {
  const {
    access,
    isAuthError,
    isReady,
    session,
  } = useAccess();
  return (
    <OptimizationJobProvider
      isAccessAuthError={isAuthError}
      isAccessReady={isReady}
      isPollingAllowed={!isBillingWorkspaceOnly(access)}
      session={session}
    >
      {children}
    </OptimizationJobProvider>
  );
}

function renderApplication(container) {
  const root = getOrCreateRoot(container);

  root.render(
    <>
      <DeploymentUpdateGuard />
      <ApplicationErrorBoundary>
        <ToastProvider>
          <AccessProvider>
            <NotificationProvider>
              <ApplicationUserUiPreferencesProvider>
                <GeotabLiveAlertsProvider>
                  <AccessBoundOptimizationJobProvider>
                    <ResultsExportProvider>
                      <TruckSheetPdfExportProvider>
                        <RouterProvider router={appRouter} fallbackElement={<AppRouterFallback />} />
                      </TruckSheetPdfExportProvider>
                    </ResultsExportProvider>
                  </AccessBoundOptimizationJobProvider>
                </GeotabLiveAlertsProvider>
              </ApplicationUserUiPreferencesProvider>
            </NotificationProvider>
          </AccessProvider>
        </ToastProvider>
      </ApplicationErrorBoundary>
    </>,
  );
  return container;
}

const container = document.querySelector("[data-react-app-root]");
if (container instanceof HTMLElement) {
  renderApplication(container);
}

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    const hotContainer = document.querySelector("[data-react-app-root]");
    if (hotContainer instanceof HTMLElement) {
      renderApplication(hotContainer);
    }
  });
}
